import { Deduction, DeductionDocument } from "@/core/entities/deduction";
import {
  Card,
  CardHeader,
  Flex,
  Heading,
  Button,
  Icon,
  Icons,
  CardContent,
  Division,
  Table,
  EmptyState,
  Paragraph,
  useToast,
} from "@ir/ui";
import { ReactNode, useState, useCallback } from "react";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DeductionRepository } from "@/infrastructure/repositories/deduction";
import { DocumentSignedURLRepository } from "@/infrastructure/repositories/document";
import { GetDocumentSignedURLUseCase } from "@/core/usecases/document";
import DeductionDocumentsTableColumns from "./DeductionDocumentsTable";
import DocumentUploadModal from "@/presentation/deduction/components/DeductionDrawer/DeductionInformation/DocumentUpload/DocumentUploadModal";
import { useDeductionDocumentUpload } from "@/presentation/deduction/components/DeductionDrawer/DeductionInformation/DocumentUpload/hook";
import { WorkspacePermissions } from "@/core/permissions";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { DeleteDeductionDocumentsUseCase } from "@/core/usecases/deduction/DeleteDeductionDocuments.useCase";
import { useConfirm, useUserPermissions, useWorkspace, useWindowSize } from "@/hooks";
import { PermissionCheckService } from "@/core/services/PermissionCheck.service";
import { useAtomValue } from "jotai";
import { groupByCheckAtom } from "@/store/deduction/main";
import { currentPageAtom as currentRemittancePageAtom } from "@/store/deduction/remittance";
import { currentPageAtom as currentRemittanceItemPageAtom } from "@/store/deduction/remittanceItem";
import { createLogger } from "@/infrastructure/services/logger";
// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
// Deduction document signed URL repository
const documentSignedURLRepository = new DocumentSignedURLRepository(client);

interface DataType {
  operation?: ReactNode;
}

const DeductionDocuments = ({ deduction }: { deduction: Deduction }) => {
  const logger = createLogger("DeductionDocuments");
  const toast = useToast();
  const { settings: workspaceSettings } = useWorkspace();
  const {
    isDeductionsLoading,
    setIsDocumentViewerOpen,
    setViewerDocuments,
    viewerDocuments,
    isDocumentViewerOpen,
    drawerInfo,
    setCloseDocumentViewer,
  } = useDeductionDrawer();
  const { width } = useWindowSize();
  const {
    isDocumentUploadOpen,
    openDocumentUpload,
    closeDocumentUpload,
    clearDocumentUpload,
    selectedModalId,
    updateDeduction,
  } = useDeductionDocumentUpload();
  const [downloadingDocuments, setDownloadingDocuments] = useState<Array<number>>([]);
  const [deletingDocuments, setDeletingDocuments] = useState<Array<number>>([]);
  const confirm = useConfirm();
  const permissionCheckService = new PermissionCheckService();
  const { permissions } = useUserPermissions();

  const groupByCheck = useAtomValue(groupByCheckAtom);
  const currentRemittancePage = useAtomValue(currentRemittancePageAtom);
  const currentRemittanceItemPage = useAtomValue(currentRemittanceItemPageAtom);
  const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;

  const fetchDocumentSignedURL = useCallback(
    async (file_url: string, file_type: string, downloadableFileName: string) => {
      const response = await new GetDocumentSignedURLUseCase(documentSignedURLRepository).execute(
        file_url,
        file_type,
        downloadableFileName
      );
      return response;
    },
    []
  );

  const handleDocumentDownload = useCallback(
    async (deductionDocument: DeductionDocument) => {
      setDownloadingDocuments(prev => [...prev, deductionDocument.id]);
      if (deductionDocument.signedUrl && !deductionDocument.isExpired) {
        setDownloadingDocuments(prev => prev.filter(id => id !== deductionDocument.id));
        globalThis.open(deductionDocument.signedUrl, "_blank");
        return;
      }

      // if the document is not deletable, then we need to use the invoice number to create the downloadable file name
      let downloadableFileName = deductionDocument.fileDisplayName;
      if (!deductionDocument.deletable) {
        const fileExtension = deductionDocument.fileDisplayName.split(".").pop();
        downloadableFileName =
          drawerInfo?.invoiceNumber && deductionDocument.documentType?.toUpperCase() === "BACKUP"
            ? `${drawerInfo?.invoiceNumber}.${fileExtension}`
            : deductionDocument.fileDisplayName;
      }

      const { signedUrl } = await fetchDocumentSignedURL(
        deductionDocument.filePath,
        deductionDocument.fileMimeType,
        downloadableFileName
      );

      setDownloadingDocuments(prev => prev.filter(id => id !== deductionDocument.id));
      const deductionDocumentIndex = deduction.documents.findIndex(d => d.id === deductionDocument.id);
      if (signedUrl) {
        deductionDocument.signedUrl = signedUrl;
        const newDocuments = [...deduction.documents];
        newDocuments[deductionDocumentIndex] = deductionDocument;
        deduction.documents = newDocuments;

        globalThis.open(deductionDocument.signedUrl, "_blank");
      } else {
        toast.error({
          content: "Failed to download document",
          icon: <Icon name={Icons.File} />,
        });
      }
    },
    [deduction, drawerInfo?.invoiceNumber, toast, fetchDocumentSignedURL]
  );

  const handleDocumentDelete = async (deductionDocumentId: number) => {
    // confirm the deletion
    const confirmResult = await confirm({
      title: "Delete Document",
      message: "Are you sure you want to delete this document?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmResult) {
      return;
    }

    try {
      setDeletingDocuments(prev => [...prev, deductionDocumentId]);
      const deleteDocumentUseCase = new DeleteDeductionDocumentsUseCase(new DeductionRepository(client));
      const response = await deleteDocumentUseCase.execute(
        {
          deduction_id: deduction.id,
          document_ids: [deductionDocumentId],
        },
        currentPage ?? 1
      );

      if (response?.deleteDeductionDocuments.id) {
        updateDeduction(response.deleteDeductionDocuments);
        setViewerDocuments(response.deleteDeductionDocuments.documents);
        toast.success({
          content: "Document deleted",
        });
      } else {
        toast.error({
          content: "Failed to delete document",
        });
      }
    } catch (error) {
      logger.error("Error deleting document", error);
      toast.error({
        content: "Failed to delete document",
      });
    } finally {
      setDeletingDocuments(prev => prev.filter(id => id !== deductionDocumentId));
    }
  };

  const columns = DeductionDocumentsTableColumns({
    isDeductionsLoading,
    workspaceSettings,
    downloadingDocuments,
    deletingDocuments,
    handleDocumentDownload,
    handleDocumentDelete,
    hasDeletePermission: permissionCheckService.hasAnyPermission(
      [WorkspacePermissions.DEDUCTION_DOCUMENT_DELETE],
      permissions
    ),
  });

  const getSkeletonData = (count: number = 2): DeductionDocument[] => {
    const currentDate = new Date().toLocaleDateString();
    return Array.from({ length: count }, (_, index) => {
      const document: DeductionDocument = new DeductionDocument({
        id: index,
        fileName: `Document ${index + 1}`,
        createdAt: currentDate,
        fileSize: 1000 + index * 1000,
        updatedAt: currentDate,
        documentType: `Document Type ${index + 1}`,
        documentTypeKey: `documentTypeKey ${index + 1}`,
        fileDisplayName: `Document ${index + 1}`,
        fileMimeType: "application/pdf",
        filePath: "",
        signedUrl: "",
        deletable: false,
      });
      return document;
    });
  };

  const handleOpenDocumentViewer = () => {
    const documents = deduction?.documents;
    deduction.documents = viewerDocuments ?? documents;
    setIsDocumentViewerOpen(true);
    setViewerDocuments(documents);
  };

  const handleCloseDocumentViewer = () => {
    setCloseDocumentViewer();
  };

  const openUploadDocument = (deductionId: number) => {
    openDocumentUpload(deductionId);
  };

  const isOpen: boolean = selectedModalId != null && selectedModalId === deduction.id && isDocumentUploadOpen;

  const closeUploadDocument = () => {
    closeDocumentUpload();
    clearDocumentUpload();
  };

  const shouldShowUploadButton =
    !isDeductionsLoading && Boolean(deduction?.id) && deduction?.isDeductionDrawerEditable();

  return (
    <Card className={`bg-white ${isDeductionsLoading ? "mb-4" : "mb-3"}`}>
      <CardHeader className="py-2">
        <Flex justifyContent="space-between" align="center">
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Documents
          </Heading>
          <Flex>
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_DOCUMENT_VIEWER]}>
              {width > 1200 && (deduction?.documents?.length ?? 0) > 0 && !isDeductionsLoading && (
                <Button
                  size="medium"
                  variant="outline"
                  color="primary"
                  onPress={isDocumentViewerOpen ? handleCloseDocumentViewer : handleOpenDocumentViewer}
                  className="mx-1"
                >
                  <Icon slot="start" name={Icons.File} />
                  {isDocumentViewerOpen ? "Hide Documents" : "View Documents"}
                </Button>
              )}
            </HasAnyPermission>
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_DOCUMENT_ADD]}>
              {shouldShowUploadButton && (
                <Button
                  size="medium"
                  variant="solid"
                  color="primary"
                  onPress={() => openUploadDocument(deduction.id)}
                  className={isDeductionsLoading || !deduction ? "button-disabled-tooltip ms-1" : "ms-1"}
                >
                  <Icon slot="start" name={Icons.UploadCloud} />
                  Upload
                </Button>
              )}
            </HasAnyPermission>
          </Flex>
        </Flex>
      </CardHeader>
      <CardContent>
        <Flex>
          <Division className="flex-1">
            {deduction?.documents?.length > 0 || isDeductionsLoading ? (
              <Table<DeductionDocument & DataType>
                columns={columns}
                dataSource={isDeductionsLoading ? getSkeletonData() : deduction?.documents || []}
                border="row"
                aria-label="Table"
                aria-labelledby="table"
                sticky={true}
                scroll={{ x: "100%" }}
                className="mb-0"
              />
            ) : (
              <EmptyState
                className="bg-white border-1 border-color-grey-25 py-3 flex-1"
                icon={Icons.File}
                title={
                  <Heading level={6} className="text-grey-700 font-bold mt-3">
                    No documents found
                  </Heading>
                }
                description={
                  <Paragraph className="text-grey-500 font-regular mb-0">
                    This is where you can view deduction documents uploaded by the client
                  </Paragraph>
                }
              />
            )}
          </Division>
        </Flex>
      </CardContent>
      <DocumentUploadModal key={deduction?.id} isOpen={isOpen} onClose={closeUploadDocument} deduction={deduction} />
    </Card>
  );
};

export default DeductionDocuments;
